// Packages
import { createSlice } from '@reduxjs/toolkit';
// Redux
import {
  getTodoAsync,
  createTodoAsync,
  getEmployeeTodoAsync,
  getTodoDetailsAsync,
  updateTodoAsync,
  deleteTodoAsync,
  deleteShiftTodoAsync,
  updateJobTodoTasksStatusAsync,
  createTodoListAsync,
  getTodoListDetailsAsync,
  updateTodoListAsync,
  getTodoListDaysAsync,
  getTodoListDayAsync,
  fixTodoListDayTaskAsync,
  getTodoTodayAsync,
  refreshTodoResultsAsync,
} from './todoThunk';
import { clearReducer } from '../auth/authThunk';
// Interfaces and types
import { IErrorRes } from 'types/appTypes';
import { TSliceReducer, TActionType } from 'redux/store';
import {
  IGetTodoRes,
  IGetEmployeeTodoRes,
  IGetTodoDetailsRes,
  ITodoListDetails,
  ITodoListDays,
  ITodoListDay,
  IGetTodoTodayRes,
} from 'types/todoTypes';

type TAction<T extends TActionType> =
  | (typeof getTodoAsync)[T]
  | (typeof createTodoAsync)[T]
  | (typeof getEmployeeTodoAsync)[T]
  | (typeof getTodoDetailsAsync)[T]
  | (typeof updateTodoAsync)[T]
  | (typeof deleteTodoAsync)[T]
  | (typeof deleteShiftTodoAsync)[T]
  | (typeof getTodoListDetailsAsync)[T];

type TSavingAction<T extends TActionType> =
  | (typeof createTodoListAsync)[T]
  | (typeof updateTodoListAsync)[T]
  | (typeof updateJobTodoTasksStatusAsync)[T]
  | (typeof fixTodoListDayTaskAsync)[T];

const handleLoadingReducer: TSliceReducer<IState, TAction<'pending'>> = state => {
  state.isLoading = true;
  state.error = null;
};

const handleEmptyFulfilledReducer: TSliceReducer<IState, TAction<'fulfilled'>> = state => {
  state.isLoading = false;
};

const handleErrorReducer: TSliceReducer<IState, TAction<'rejected'>> = (state, action) => {
  state.isLoading = false;
  state.error = action.payload || null;
};

// Not isLoading: the details page shows a loader on it, which would unmount the open modal.
const handleSavingReducer: TSliceReducer<IState, TSavingAction<'pending'>> = state => {
  state.isSaving = true;
  state.error = null;
};

const handleSavedReducer: TSliceReducer<IState, TSavingAction<'fulfilled'>> = state => {
  state.isSaving = false;
};

const handleSavingErrorReducer: TSliceReducer<IState, TSavingAction<'rejected'>> = (
  state,
  action,
) => {
  state.isSaving = false;
  state.error = action.payload || null;
};

interface IState {
  todo: IGetTodoRes | null;
  employeeTodo: IGetEmployeeTodoRes | null;
  todoDetails: IGetTodoDetailsRes | null;
  todoListDetails: ITodoListDetails | null;
  todoListDays: ITodoListDays | null;
  todoListDay: ITodoListDay | null;
  todoToday: IGetTodoTodayRes | null;
  error: IErrorRes | null;
  isLoading: boolean;
  isSaving: boolean;
  isDayLoading: boolean;
  isTodayLoading: boolean;
}

const initialState: IState = {
  todo: null,
  todoDetails: null,
  todoListDetails: null,
  todoListDays: null,
  todoListDay: null,
  todoToday: null,
  employeeTodo: null,
  error: null,
  isLoading: false,
  isSaving: false,
  isDayLoading: false,
  isTodayLoading: false,
};

const todoSlice = createSlice({
  name: 'todo',
  initialState,
  reducers: {},
  extraReducers: builder => {
    //* GET TODO ASYNC THUNK
    builder.addCase(getTodoAsync.pending, handleLoadingReducer);
    builder.addCase(getTodoAsync.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.todo = payload;
    });
    builder.addCase(getTodoAsync.rejected, handleErrorReducer);

    //* CREATE TODO ASYNC THUNK
    builder.addCase(createTodoAsync.pending, handleLoadingReducer);
    builder.addCase(createTodoAsync.fulfilled, handleEmptyFulfilledReducer);
    builder.addCase(createTodoAsync.rejected, handleErrorReducer);

    //* GET EMPLOYEE TODO ASYNC THUNK
    builder.addCase(getEmployeeTodoAsync.pending, handleLoadingReducer);
    builder.addCase(getEmployeeTodoAsync.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.employeeTodo = payload;
    });
    builder.addCase(getEmployeeTodoAsync.rejected, handleErrorReducer);

    //* GET EMPLOYEE TODO DETAILS ASYNC THUNK
    builder.addCase(getTodoDetailsAsync.pending, handleLoadingReducer);
    builder.addCase(getTodoDetailsAsync.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.todoDetails = payload;
    });
    builder.addCase(getTodoDetailsAsync.rejected, handleErrorReducer);

    //* UPDATE TODO ASYNC THUNK
    builder.addCase(updateTodoAsync.pending, handleLoadingReducer);
    builder.addCase(updateTodoAsync.fulfilled, handleEmptyFulfilledReducer);
    builder.addCase(updateTodoAsync.rejected, handleErrorReducer);

    //* UPDATE THE RESULTS OF A TODO ASYNC THUNK (a job list's modal, a tick made in place on a user list)
    // Saving, not loading: the details page puts a loader over everything while `isLoading` is set,
    // which a tick on one item must not do.
    builder.addCase(updateJobTodoTasksStatusAsync.pending, handleSavingReducer);
    builder.addCase(updateJobTodoTasksStatusAsync.fulfilled, handleSavedReducer);
    builder.addCase(updateJobTodoTasksStatusAsync.rejected, handleSavingErrorReducer);

    //* QUIET RELOAD OF A LIST AFTER A TICK ASYNC THUNK
    builder.addCase(refreshTodoResultsAsync.fulfilled, (state, { payload }) => {
      state.todoDetails = payload.details;
      state.todoListDetails = payload.list;
      // A reload is also how a manager dismisses the version-conflict banner.
      state.error = null;
    });

    //* DELETE EMPLOYEE TODO ASYNC THUNK
    builder.addCase(deleteTodoAsync.pending, handleLoadingReducer);
    builder.addCase(deleteTodoAsync.fulfilled, state => {
      state.isLoading = false;
      state.todoDetails = null;
    });
    builder.addCase(deleteTodoAsync.rejected, handleErrorReducer);

    //* DELETE SHIFT TODO ASYNC THUNK
    builder.addCase(deleteShiftTodoAsync.pending, handleLoadingReducer);
    builder.addCase(deleteShiftTodoAsync.fulfilled, state => {
      state.isLoading = false;
      state.todoDetails = null;
    });
    builder.addCase(deleteShiftTodoAsync.rejected, handleErrorReducer);

    //* UPDATE TODO ASYNC THUNK

    //* CREATE TODO LIST ASYNC THUNK
    builder.addCase(createTodoListAsync.pending, handleSavingReducer);
    builder.addCase(createTodoListAsync.fulfilled, handleSavedReducer);
    builder.addCase(createTodoListAsync.rejected, handleSavingErrorReducer);

    //* GET TODO LIST DETAILS ASYNC THUNK
    builder.addCase(getTodoListDetailsAsync.pending, state => {
      state.isLoading = true;
      state.error = null;
      state.todoListDays = null;
      state.todoListDay = null;
    });
    builder.addCase(getTodoListDetailsAsync.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.todoListDetails = payload;
    });
    builder.addCase(getTodoListDetailsAsync.rejected, handleErrorReducer);

    //* UPDATE TODO LIST ASYNC THUNK
    builder.addCase(updateTodoListAsync.pending, handleSavingReducer);
    builder.addCase(updateTodoListAsync.fulfilled, handleSavedReducer);
    builder.addCase(updateTodoListAsync.rejected, handleSavingErrorReducer);

    //* GET TODO LIST DAYS ASYNC THUNK
    builder.addCase(getTodoListDaysAsync.fulfilled, (state, { payload }) => {
      state.todoListDays = payload;
    });

    //* GET TODO LIST DAY ASYNC THUNK
    builder.addCase(getTodoListDayAsync.pending, state => {
      state.isDayLoading = true;
    });
    builder.addCase(getTodoListDayAsync.fulfilled, (state, { payload }) => {
      state.isDayLoading = false;
      state.todoListDay = payload;
    });
    builder.addCase(getTodoListDayAsync.rejected, state => {
      state.isDayLoading = false;
    });

    //* FIX A TICK OF A PAST DAY ASYNC THUNK
    builder.addCase(fixTodoListDayTaskAsync.pending, handleSavingReducer);
    builder.addCase(fixTodoListDayTaskAsync.fulfilled, handleSavedReducer);
    builder.addCase(fixTodoListDayTaskAsync.rejected, handleSavingErrorReducer);

    //* GET TODAY'S LISTS ASYNC THUNK
    builder.addCase(getTodoTodayAsync.pending, state => {
      state.isTodayLoading = true;
      state.error = null;
    });
    builder.addCase(getTodoTodayAsync.fulfilled, (state, { payload }) => {
      state.isTodayLoading = false;
      state.todoToday = payload;
    });
    builder.addCase(getTodoTodayAsync.rejected, (state, action) => {
      state.isTodayLoading = false;
      state.error = action.payload || null;
    });

    //* CLEAR REDUCER AFTER SIGN OUT
    builder.addCase(clearReducer, () => initialState);
  },
});

export default todoSlice;
